import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  leadsApi,
  Lead,
  LeadDetail,
  LeadStatus,
  LeadSubStatus,
  LeadUpdatePayload,
} from "@/api/leads";
import { useAuth } from "@/contexts/AuthContext";
import {
  isCrmViewOnlyUser,
  shouldMaskLeadPhone,
  canElevatedLeadAdmin,
  canDeleteLeadCallRecording,
} from "@/utils/crmAccess";
import { maskPhoneDisplay } from "@/utils/stringUtils";
import { leadCampaignLabel } from "@/lib/leadCampaignDisplay";
import { LeadBantScoringCard } from "@/components/crm/LeadBantScoringCard";
import {
  isLikelyAudioAttachment,
  LeadActivityAudioPlayer,
  isExternalDirectRecordingUrl,
} from "@/components/crm/LeadActivityAudioPlayer";
import {
  LEAD_STATUS_OPTIONS,
  LEAD_SUBSTATUS_OPTIONS_BY_STATUS,
  formatLeadStatusLabel,
  formatLeadSubStatusLabel,
} from "@/lib/crmLeadStyles";
import { MEDICAL_SPECIALISATIONS } from "@/lib/medicalSpecialisations";
import { AESTHETIC_PROCEDURE_OPTIONS } from "@/lib/aestheticProcedure";
import { CANONICAL_LEAD_SOURCES, formatLeadSourceLabel, toLeadSourceSearchableOptions } from "@/lib/leadSources";
import { normalizePersonName, normalizePersonNameOptional } from "@/lib/personName";
import { LeadSourceSearchableSelect } from "@/components/common/LeadSourceSearchableSelect";
import {
  istDateInputToIso,
  istDatetimeLocalToIso,
  toIstDateInputValue,
  toIstDatetimeLocalValue,
} from "@/utils/dateUtils";
import { getApiErrorMessage } from "@/utils/apiErrorMessage";
import { isActiveCustomerCallbackAt } from "@/utils/lead-customer-callback.util";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Pencil, Check, X, Paperclip, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import "@/styles/lead-detail-artis.css";
import {
  LeadDetailCard,
  LeadDetailFieldLabel,
  LD_INPUT,
  LD_TEXTAREA,
  LD_NATIVE_SELECT,
  LD_SELECT_TRIGGER,
} from "@/components/crm/leadDetailArtisUi";
import { cn } from "@/lib/utils";

const URL_IN_TEXT = /(https?:\/\/[^\s<>"']+)/g;

const CONTACT_RELATIONSHIP_OPTIONS = [
  'Husband',
  'Wife',
  'Son',
  'Daughter',
  'Brother',
  'Sister',
  'Father',
  'Mother',
  'Friend',
  'Partner',
  'Relative',
  'Self',
  'Other',
] as const;

function approximateAgeFromDob(iso?: string | null): string | null {
  if (!iso) return null;
  const birth = new Date(iso);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let ageYears = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) ageYears -= 1;
  return ageYears >= 0 ? String(ageYears) : null;
}

function isOpdVisitedOrAbove(ss?: LeadSubStatus | null): boolean {
  return (
    ss === 'OPD_VISITED' ||
    ss === 'IPD_ADMITTED' ||
    ss === 'PROCEDURE_BOOKED' ||
    ss === 'DAY_CARE_DONE'
  );
}

function wasEverOpdVisited(lead: {
  opdVisitedAt?: string | null;
  leadSubStatus?: LeadSubStatus | null;
}): boolean {
  return !!lead.opdVisitedAt || lead.leadSubStatus === 'OPD_VISITED';
}

function formatIstDateLabel(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' });
}

function isEditableManualHistoryEntry(
  entry: {
    id?: string;
    createdBy?: { id: string; name: string } | null;
    attachmentUrl?: string | null;
  },
  currentUserId?: string | null,
  agencyViewOnly?: boolean
) {
  return !agencyViewOnly && !!entry.id && !entry.attachmentUrl && !!currentUserId && entry.createdBy?.id === currentUserId;
}

/** True if IST datetime-local string parses to a time strictly after `now`. */
function isFollowUpLocalStrictlyFuture(local: string, now: Date = new Date()): boolean {
  const t = new Date(`${local.trim()}:00+05:30`).getTime();
  return Number.isFinite(t) && t > now.getTime();
}

/** Patient list route for CRM "OPD Booked" → register / book flow */
function crmPatientsPathForOpdBooking(role: string | undefined): string | null {
  if (role === 'SALES_PERSON' || role === 'SALES_MANAGER') return '/sales-person/patients';
  if (role === 'HOSPITAL_ADMIN') return '/hospital-admin/patients';
  if (role === 'RECEPTIONIST') return '/receptionist/patients';
  return null;
}

function baselineLeadAgeInput(d: LeadDetail): string {
  if (d.age != null && Number.isFinite(d.age)) return String(d.age);
  return approximateAgeFromDob(d.dob ?? null) ?? '';
}

type DetailFormState = {
  status: LeadStatus;
  nextFollowUpLocal: string;
  leadSubStatus: LeadSubStatus | null;
  isSurgeryCase: 'unset' | 'yes' | 'no';
  tentativeSurgeryDateLocal: string;
  aestheticProcedure: string;
  alternatePhone: string;
  email: string;
  ageLocal: string;
  gender: 'MALE' | 'FEMALE' | 'OTHER' | 'unset';
  profession: string;
  location: string;
  contactPersonName: string;
  contactRelationship: string;
  /** Canonical value matching a `SelectItem` in the Exotel source picker */
  source: string;
  specialisation: string;
  notes: string;
};

function detailFormFromLead(d: LeadDetail): DetailFormState {
  return {
    status: d.status,
    nextFollowUpLocal: toIstDatetimeLocalValue(d.nextFollowUpAt),
    leadSubStatus: d.leadSubStatus ?? null,
    isSurgeryCase:
      d.isSurgeryCase === true ? 'yes' : d.isSurgeryCase === false ? 'no' : 'unset',
    tentativeSurgeryDateLocal: toIstDateInputValue(d.tentativeSurgeryDate),
    aestheticProcedure: d.aestheticProcedureName?.trim() ?? '',
    alternatePhone: d.alternatePhone ?? '',
    email: d.email?.trim() ?? '',
    ageLocal: baselineLeadAgeInput(d),
    gender: d.gender ?? 'unset',
    profession: d.profession ?? '',
    location: d.location ?? '',
    contactPersonName: d.contactPersonName ?? '',
    contactRelationship: d.contactRelationship?.trim() ?? '',
    source: normLeadSourceKey(d.source),
    specialisation: d.specialisation?.trim() ?? '',
    notes: d.notes?.trim() ?? '',
  };
}

function isDetailDirty(d: LeadDetail, f: DetailFormState, role?: string): boolean {
  const nextLocal = toIstDatetimeLocalValue(d.nextFollowUpAt);
  const surg =
    d.isSurgeryCase === true ? 'yes' : d.isSurgeryCase === false ? 'no' : 'unset';
  const tDate = toIstDateInputValue(d.tentativeSurgeryDate);
  const baseAge = baselineLeadAgeInput(d);
  const storedProc = (d.aestheticProcedureName ?? '').trim();
  const storedRel = (d.contactRelationship ?? '').trim();
  const statusNeedsFollowUp = f.status !== 'LOST' && f.status !== 'CLOSED';
  const fuEmpty = !f.nextFollowUpLocal.trim();
  const noServerFu = !d.nextFollowUpAt;
  const staleRequiredFollowUp = statusNeedsFollowUp && fuEmpty && noServerFu;
  const effectiveTentativeLocal =
    f.isSurgeryCase === 'yes' ? (f.tentativeSurgeryDateLocal || '').trim() : '';
  const tentativeDirty = effectiveTentativeLocal !== tDate;
  const effectiveProcLocal = f.isSurgeryCase === 'yes' ? (f.aestheticProcedure || '').trim() : '';
  const procDirty = effectiveProcLocal !== storedProc;
  const notesDirty = (f.notes || '').trim() !== (d.notes || '').trim();
  return (
    staleRequiredFollowUp ||
    notesDirty ||
    f.status !== d.status ||
    f.nextFollowUpLocal !== nextLocal ||
    (f.leadSubStatus ?? null) !== (d.leadSubStatus ?? null) ||
    f.isSurgeryCase !== surg ||
    tentativeDirty ||
    procDirty ||
    (f.alternatePhone || '').trim() !== (d.alternatePhone ?? '').trim() ||
    (f.email || '').trim().toLowerCase() !== (d.email ?? '').trim().toLowerCase() ||
    (f.ageLocal || '').trim() !== baseAge.trim() ||
    f.gender !== (d.gender ?? 'unset') ||
    (f.profession || '').trim() !== (d.profession ?? '').trim() ||
    (f.location || '').trim() !== (d.location ?? '').trim() ||
    (f.contactPersonName || '').trim() !== (d.contactPersonName ?? '').trim() ||
    (f.contactRelationship || '').trim() !== storedRel ||
    ((canAdminChangeLeadSource(role) || isExotelLeadSource(d.source)) &&
      normLeadSourceKey(f.source) !== normLeadSourceKey(d.source)) ||
    (f.specialisation || '').trim() !== (d.specialisation ?? '').trim()
  );
}

function activityTypeDisplayLabel(type: string): string {
  if (type === 'ATTACHMENT') return 'Attachment';
  return type;
}

const IMAGE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'bmp',
  'svg',
  'ico',
  'avif',
  'heic',
  'heif',
]);

function extensionFromPath(path: string): string {
  const clean = path.split(/[?#]/)[0] ?? '';
  const i = clean.lastIndexOf('.');
  return i >= 0 ? clean.slice(i + 1).toLowerCase() : '';
}

/** MIME from server, or infer from filename / URL when MIME is missing (common with some storages). */
function isLikelyImageAttachment(
  mime?: string | null,
  filename?: string | null,
  url?: string | null
): boolean {
  if (mime?.startsWith('image/')) return true;
  const extName = filename ? extensionFromPath(filename) : '';
  if (extName && IMAGE_EXTENSIONS.has(extName)) return true;
  const extUrl = url ? extensionFromPath(url) : '';
  if (extUrl && IMAGE_EXTENSIONS.has(extUrl)) return true;
  return false;
}

function normLeadSourceKey(s: string | null | undefined): string {
  return String(s ?? '').trim().toUpperCase();
}

function isExotelLeadSource(s: string | null | undefined): boolean {
  return normLeadSourceKey(s) === 'EXOTEL';
}

function canAdminChangeLeadSource(role: string | undefined): boolean {
  return role === 'HOSPITAL_ADMIN' || role === 'SUPER_ADMIN';
}

function showLeadSourceEditor(
  role: string | undefined,
  leadSource: string | null | undefined,
  formSource: string
): boolean {
  if (canAdminChangeLeadSource(role)) return true;
  return isExotelLeadSource(leadSource) || isExotelLeadSource(formSource);
}

/** Dropdown options: full medical list plus any stored/draft value not in the list. */
function medicalSpecialisationPicklist(
  currentStored: string | null | undefined,
  draft: string | null | undefined
): string[] {
  const listed = new Set(MEDICAL_SPECIALISATIONS);
  const extra: string[] = [];
  for (const raw of [currentStored, draft]) {
    const t = String(raw ?? '').trim();
    if (t && !listed.has(t) && !extra.includes(t)) extra.push(t);
  }
  extra.sort((a, b) => a.localeCompare(b));
  return [...extra, ...MEDICAL_SPECIALISATIONS];
}

function TimelineAttachmentPreview({
  url,
  filename,
  mime,
  activityId,
  recordingId,
}: {
  url: string;
  filename?: string | null;
  mime?: string | null;
  activityId?: string;
  recordingId?: string;
}) {
  const [imgBroken, setImgBroken] = useState(false);
  const showThumb = isLikelyImageAttachment(mime, filename, url) && !imgBroken;
  const label = filename?.trim() ? filename : 'Download file';

  if (isLikelyAudioAttachment(mime, filename, url)) {
    const proxyExotel =
      !!activityId && !recordingId && isExternalDirectRecordingUrl(url);
    return (
      <LeadActivityAudioPlayer
        activityId={proxyExotel ? undefined : activityId}
        recordingId={recordingId}
        exotelActivityId={proxyExotel ? activityId : undefined}
        directUrl={!activityId && !recordingId && !proxyExotel ? url : undefined}
        label="Play call recording"
      />
    );
  }

  return (
    <div className="mt-2 flex flex-wrap items-start gap-3">
      {showThumb ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 rounded-md ring-1 ring-slate-200/80 transition hover:ring-indigo-300"
          title="Open image"
        >
          <img
            src={url}
            alt={filename || 'Image attachment'}
            loading="lazy"
            onError={() => setImgBroken(true)}
            className="h-14 w-14 rounded-md border border-slate-200 bg-slate-50 object-cover shadow-sm sm:h-16 sm:w-16"
          />
        </a>
      ) : null}
      <div className="min-w-0 flex-1">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 underline hover:text-indigo-800 break-all"
        >
          📎 {label}
        </a>
      </div>
    </div>
  );
}

function ActivitySummaryWithLinks({ text }: { text: string }) {
  const parts = text.split(URL_IN_TEXT);
  return (
    <p className="text-gray-700 break-words whitespace-pre-wrap">
      {parts.map((part, i) =>
        /^https?:\/\//.test(part) ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline hover:text-blue-800 break-all"
          >
            {part}
          </a>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </p>
  );
}

export type LeadDetailModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** List row used to open the modal (id + display fields). */
  anchorLead: Lead | null;
  /** Ordered leads for prev/next (e.g. current table page). */
  navLeads?: Lead[];
};

export function LeadDetailModal({ open, onOpenChange, anchorLead, navLeads = [] }: LeadDetailModalProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const agencyViewOnly = isCrmViewOnlyUser(user);
  const elevatedLeadAdmin = canElevatedLeadAdmin(user);
  const canDeleteRecording = canDeleteLeadCallRecording(user);
  const formatLeadPhone = (phone?: string | null) =>
    shouldMaskLeadPhone(user) ? maskPhoneDisplay(phone) : phone || '—';

  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [activitySummary, setActivitySummary] = useState('');
  const [activityType, setActivityType] = useState('CALL');
  const [waFreeformText, setWaFreeformText] = useState('');
  const [waMediaFile, setWaMediaFile] = useState<File | null>(null);
  const [waMediaCaption, setWaMediaCaption] = useState('');
  const [waTemplateName, setWaTemplateName] = useState('');
  const [waTemplateParamsText, setWaTemplateParamsText] = useState('');
  const [waTemplateLanguage, setWaTemplateLanguage] = useState('en');
  const [waTemplateHeaderFile, setWaTemplateHeaderFile] = useState<File | null>(null);
  const [editingActivityId, setEditingActivityId] = useState<string | null>(null);
  const [editingActivityType, setEditingActivityType] = useState('CALL');
  const [editingActivitySummary, setEditingActivitySummary] = useState('');
  const [editingCampaignName, setEditingCampaignName] = useState(false);
  const [campaignNameDraft, setCampaignNameDraft] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [recordingDeleteConfirmId, setRecordingDeleteConfirmId] = useState<string | null>(null);

  const updateLeadMut = useMutation({
    mutationFn: (payload: { id: string; data: LeadUpdatePayload }) =>
      leadsApi.updateLead(payload.id, payload.data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['crm-ipd-cases'] });
      queryClient.invalidateQueries({ queryKey: ['crm-ipd-admitted'] });
      queryClient.invalidateQueries({ queryKey: ['lead-filter-options'] });
      const updated = res.data?.data as Lead | undefined;
      if (updated) {
        setLeadDetail((d) => (d && d.id === updated.id ? { ...d, ...updated } : d));
        setSelectedLead((s) => (s && s.id === updated.id ? { ...s, ...updated } : s));
      }
    },
  });

  const deleteCallRecordingMut = useMutation({
    mutationFn: (recordingId: string) => leadsApi.deleteCallRecording(recordingId),
    onSuccess: async () => {
      setRecordingDeleteConfirmId(null);
      toast.success('Call recording deleted');
      const leadId = leadDetail?.id;
      if (leadId) {
        try {
          const res = await leadsApi.getLeadById(leadId);
          setLeadDetail(res.data.data);
        } catch {
          /* keep existing detail if refetch fails */
        }
      }
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: (e: unknown) => {
      toast.error(getApiErrorMessage(e, 'Could not delete recording'));
    },
  });

  const deleteLeadMut = useMutation({
    mutationFn: (id: string) => leadsApi.deleteLead(id),
    onSuccess: (_res, id) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['crm-ipd-cases'] });
      queryClient.invalidateQueries({ queryKey: ['crm-ipd-admitted'] });
      queryClient.invalidateQueries({ queryKey: ['lead-filter-options'] });
      toast.success('Lead deleted');
      setDeleteConfirmOpen(false);
      setEditingCampaignName(false);
      onOpenChange(false);
      setSelectedLead((s) => (s?.id === id ? null : s));
      setLeadDetail((d) => (d?.id === id ? null : d));
    },
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message || 'Could not delete lead');
    },
  });

  const addActivityMut = useMutation({
    mutationFn: (payload: { id: string; data: { type: string; summary?: string } }) =>
      leadsApi.addActivity(payload.id, payload.data),
    onSuccess: async (res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['crm-ipd-cases'] });
      queryClient.invalidateQueries({ queryKey: ['crm-ipd-admitted'] });
      const payload = res.data?.data as
        | { notReachableCadenceReset?: boolean; notReachableAttemptTotal?: number }
        | undefined;
      try {
        const fresh = await leadsApi.getLeadById(variables.id);
        setLeadDetail(fresh.data.data);
      } catch {
        /* keep existing detail if refetch fails */
      }
      setActivitySummary('');
      if (payload?.notReachableCadenceReset) {
        toast.success('Activity added — not-reachable counter reset to 0/9 (contact logged).');
      } else {
        toast.success('Activity added');
      }
    },
  });

  const updateActivityMut = useMutation({
    mutationFn: (payload: { activityId: string; data: { type?: string; summary?: string } }) =>
      leadsApi.updateActivity(payload.activityId, payload.data),
    onSuccess: async (_res, _variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['crm-ipd-cases'] });
      queryClient.invalidateQueries({ queryKey: ['crm-ipd-admitted'] });
      const leadId = leadDetail?.id ?? selectedLead?.id;
      if (leadId) {
        try {
          const fresh = await leadsApi.getLeadById(leadId);
          setLeadDetail(fresh.data.data);
        } catch {
          /* keep existing detail if refetch fails */
        }
      }
      setEditingActivityId(null);
      setEditingActivitySummary('');
      setEditingActivityType('CALL');
      toast.success('History entry updated');
    },
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message || 'Could not update history entry');
    },
  });

  const deleteActivityMut = useMutation({
    mutationFn: (activityId: string) => leadsApi.deleteActivity(activityId),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['crm-ipd-cases'] });
      queryClient.invalidateQueries({ queryKey: ['crm-ipd-admitted'] });
      const leadId = leadDetail?.id ?? selectedLead?.id;
      if (leadId) {
        try {
          const fresh = await leadsApi.getLeadById(leadId);
          setLeadDetail(fresh.data.data);
        } catch {
          /* keep existing detail if refetch fails */
        }
      }
      setEditingActivityId(null);
      setEditingActivitySummary('');
      setEditingActivityType('CALL');
      toast.success('History entry deleted');
    },
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message || 'Could not delete history entry');
    },
  });

  const incrementNotReachableCallMut = useMutation({
    mutationFn: (leadId: string) => leadsApi.incrementNotReachableCall(leadId),
    onSuccess: async (res, leadId) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['crm-ipd-cases'] });
      queryClient.invalidateQueries({ queryKey: ['crm-ipd-admitted'] });
      const payload = res.data?.data;
      if (payload?.lead) {
        const updated = payload.lead;
        setLeadDetail((d) => (d && d.id === updated.id ? { ...d, ...updated } : d));
        setSelectedLead((s) => (s && s.id === updated.id ? { ...s, ...updated } : s));
        setDetailForm((f) => {
          if (!f) return f;
          return {
            ...f,
            status: updated.status as LeadStatus,
            leadSubStatus: (updated.leadSubStatus ?? null) as LeadSubStatus | null,
            nextFollowUpLocal: toIstDatetimeLocalValue(updated.nextFollowUpAt),
          };
        });
      }
      try {
        const fresh = await leadsApi.getLeadById(leadId);
        setLeadDetail(fresh.data.data);
      } catch {
        /* keep merged detail */
      }
      if (payload?.autoLost) {
        toast.success('9 not-reachable calls logged — lead moved to Lost (long-term follow-up).');
      } else {
        toast.success(`Logged (${payload?.notReachableAttemptTotal ?? '—'}/9, today ${payload?.notReachableAttemptsToday ?? '—'}/3 IST).`);
      }
    },
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message || 'Could not log not-reachable call');
    },
  });

  const activityFileRef = useRef<HTMLInputElement>(null);

  const sendLeadWhatsAppMut = useMutation({
    mutationFn: (payload: {
      leadId: string;
      text?: string;
      templateName?: string;
      templateLanguage?: string;
      templateBodyParams?: string[];
    }) => leadsApi.sendLeadWhatsApp(payload.leadId, payload),
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['crm-ipd-cases'] });
      queryClient.invalidateQueries({ queryKey: ['crm-ipd-admitted'] });
      try {
        const res = await leadsApi.getLeadById(variables.leadId);
        setLeadDetail(res.data.data);
      } catch {
        /* keep detail */
      }
      setWaFreeformText('');
      setWaTemplateName('');
      setWaTemplateParamsText('');
      toast.success('WhatsApp sent');
    },
    onError: (err: unknown) => {
      toast.error(getApiErrorMessage(err, 'Could not send WhatsApp'));
    },
  });

  const sendLeadWhatsAppMediaMut = useMutation({
    mutationFn: (payload: {
      leadId: string;
      file: File;
      caption?: string;
      templateName?: string;
      templateLanguage?: string;
      templateBodyParams?: string[];
    }) => leadsApi.sendLeadWhatsAppMedia(payload.leadId, payload),
    onSuccess: async () => {
      toast.success('WhatsApp media sent');
      setWaMediaFile(null);
      setWaMediaCaption('');
      setWaTemplateHeaderFile(null);
      await queryClient.invalidateQueries({ queryKey: ['leadDetail'] });
      await queryClient.invalidateQueries({ queryKey: ['leads'] });
      await queryClient.invalidateQueries({ queryKey: ['crm-ipd-cases'] });
    },
    onError: (err: unknown) => {
      toast.error(getApiErrorMessage(err, 'Could not send WhatsApp media'));
    },
  });

  const uploadLeadAttachmentMut = useMutation({
    mutationFn: (payload: { leadId: string; file: File; summary?: string }) =>
      leadsApi.uploadLeadActivityFile(payload.leadId, payload.file, payload.summary),
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['crm-ipd-cases'] });
      queryClient.invalidateQueries({ queryKey: ['crm-ipd-admitted'] });
      try {
        const res = await leadsApi.getLeadById(variables.leadId);
        setLeadDetail(res.data.data);
      } catch {
        /* keep existing detail if refetch fails */
      }
      if (activityFileRef.current) {
        activityFileRef.current.value = '';
      }
      toast.success('File added to timeline');
    },
    onError: (err: unknown) => {
      const ax = err as { response?: { status?: number; data?: { message?: string } } };
      const serverMsg = ax.response?.data?.message;
      if (ax.response?.status && ax.response.status >= 500) {
        toast.error(
          serverMsg ||
            'Upload failed (server). Storage (S3) or database may be misconfigured — check server logs.'
        );
      } else {
        toast.error(
          serverMsg || 'Upload failed — try a smaller file or allowed type (PDF, images, Office)'
        );
      }
    },
  });

  const [leadDetail, setLeadDetail] = useState<LeadDetail | null>(null);
  const [detailForm, setDetailForm] = useState<DetailFormState | null>(null);
  const [editingPatientName, setEditingPatientName] = useState(false);
  const [patientNameDraft, setPatientNameDraft] = useState('');
  const [detailFetching, setDetailFetching] = useState(false);

  const aestheticProcedureDropdownOptions = useMemo(() => {
    const v = detailForm?.aestheticProcedure?.trim();
    const base = [...AESTHETIC_PROCEDURE_OPTIONS] as string[];
    if (v && !base.includes(v)) base.push(v);
    return base;
  }, [detailForm?.aestheticProcedure]);

  const contactRelationshipDropdownOptions = useMemo(() => {
    const v = detailForm?.contactRelationship?.trim();
    const base = [...CONTACT_RELATIONSHIP_OPTIONS] as string[];
    if (v && !base.includes(v)) base.push(v);
    return base;
  }, [detailForm?.contactRelationship]);

  const startEditingHistoryEntry = (entry: {
    id: string;
    summary?: string | null;
    activityType?: string;
    type?: string;
  }) => {
    setEditingActivityId(entry.id);
    setEditingActivityType(entry.activityType || entry.type || 'CALL');
    setEditingActivitySummary(entry.summary ?? '');
  };

  const specialisationDropdownOptions = useMemo(
    () =>
      medicalSpecialisationPicklist(leadDetail?.specialisation, detailForm?.specialisation),
    [leadDetail?.specialisation, detailForm?.specialisation]
  );

  const { data: leadFilterOptions } = useQuery({
    queryKey: ['lead-filter-options', user?.hospitalId],
    queryFn: () => leadsApi.getLeadFilterOptions(),
    enabled: !!user?.hospitalId && open,
  });

  const allLeadSourcesForSelect = useMemo(() => {
    const fromApi = leadFilterOptions?.sources ?? [];
    const byUpper = new Map<string, string>();
    for (const s of fromApi) {
      const t = String(s).trim();
      if (!t) continue;
      const u = normLeadSourceKey(t);
      if (!byUpper.has(u)) byUpper.set(u, t);
    }
    for (const s of CANONICAL_LEAD_SOURCES) {
      const u = normLeadSourceKey(s.value);
      if (!byUpper.has(u)) byUpper.set(u, s.value);
    }
    return [...byUpper.values()].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [leadFilterOptions?.sources]);

  const leadSourceSelectOptions = useMemo(
    () =>
      toLeadSourceSearchableOptions(
        allLeadSourcesForSelect.map((s) => ({
          value: normLeadSourceKey(s),
          label: formatLeadSourceLabel(s),
        }))
      ),
    [allLeadSourcesForSelect]
  );

  const detailNavIndex = useMemo(() => {
    const id = leadDetail?.id ?? selectedLead?.id;
    if (!id) return -1;
    return navLeads.findIndex((l) => l.id === id);
  }, [navLeads, leadDetail?.id, selectedLead?.id]);

  const openLeadDetail = async (lead: Lead) => {
    const switchingAway = open && leadDetail != null && lead.id !== leadDetail.id;
    if (switchingAway && detailForm != null && isDetailDirty(leadDetail, detailForm, user?.role)) {
      if (!window.confirm('You have unsaved changes. Leave this lead?')) return;
    }

    setSelectedLead(lead);
    if (switchingAway) {
      setLeadDetail(null);
      setDetailForm(null);
      setEditingPatientName(false);
      setPatientNameDraft('');
      setEditingCampaignName(false);
      setCampaignNameDraft('');
      setDeleteConfirmOpen(false);
    }

    setDetailFetching(true);
    try {
      const res = await leadsApi.getLeadById(lead.id);
      const d = res.data.data;
      setLeadDetail(d);
      setDetailForm(detailFormFromLead(d));
    } catch {
      setLeadDetail(null);
      setDetailForm(null);
    } finally {
      setDetailFetching(false);
    }
  };

  const goAdjacentLead = (dir: -1 | 1) => {
    if (detailFetching) return;
    const idx = detailNavIndex;
    if (idx < 0) return;
    const next = navLeads[idx + dir];
    if (!next) return;
    void openLeadDetail(next);
  };

  const handleDetailSubmit = () => {
    if (!detailForm || !leadDetail || agencyViewOnly) return;
    if (
      detailForm.status !== 'LOST' &&
      detailForm.status !== 'CLOSED' &&
      !detailForm.nextFollowUpLocal.trim()
    ) {
      toast.error('Next follow-up is required unless lead status is Lost or Closed.');
      return;
    }
    if (
      detailForm.status === 'LOST' &&
      leadDetail.status !== 'LOST' &&
      wasEverOpdVisited(leadDetail) &&
      detailForm.notes.trim().length < 3
    ) {
      toast.error(
        'Please enter the reason for Lost in the notes section (required for OPD visited cases).'
      );
      return;
    }
    if (detailForm.nextFollowUpLocal.trim()) {
      if (!isFollowUpLocalStrictlyFuture(detailForm.nextFollowUpLocal.trim())) {
        toast.error('Next follow-up must be a future date and time.');
        return;
      }
    }
    const rawAge = detailForm.ageLocal.trim();
    const parsedAge =
      rawAge === ''
        ? null
        : (() => {
            const n = Number.parseInt(rawAge, 10);
            return Number.isFinite(n) && n >= 0 ? n : null;
          })();
    const emailTrim = detailForm.email.trim();
    if (emailTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      toast.error('Enter a valid email address.');
      return;
    }
    updateLeadMut.mutate(
      {
        id: leadDetail.id,
        data: {
          status: detailForm.status,
          leadSubStatus: detailForm.leadSubStatus,
          notes: detailForm.notes.trim() || null,
          nextFollowUpAt: detailForm.nextFollowUpLocal.trim()
            ? istDatetimeLocalToIso(detailForm.nextFollowUpLocal)
            : null,
          age: parsedAge,
          email: emailTrim || null,
          gender: detailForm.gender === 'unset' ? null : detailForm.gender,
          profession: detailForm.profession.trim() || null,
          location: detailForm.location.trim() || null,
          isSurgeryCase:
            detailForm.isSurgeryCase === 'unset' ? null : detailForm.isSurgeryCase === 'yes',
          aestheticProcedureName:
            detailForm.isSurgeryCase === 'yes' && detailForm.aestheticProcedure.trim()
              ? detailForm.aestheticProcedure.trim()
              : null,
          tentativeSurgeryDate:
            detailForm.isSurgeryCase === 'yes' && detailForm.tentativeSurgeryDateLocal
              ? istDateInputToIso(detailForm.tentativeSurgeryDateLocal)
              : null,
          alternatePhone: detailForm.alternatePhone.trim() || null,
          contactPersonName: normalizePersonNameOptional(detailForm.contactPersonName),
          contactRelationship: detailForm.contactRelationship.trim() || null,
          specialisation: detailForm.specialisation.trim() || null,
          ...(normLeadSourceKey(detailForm.source) !== normLeadSourceKey(leadDetail.source) &&
          (canAdminChangeLeadSource(user?.role) || isExotelLeadSource(leadDetail.source))
            ? { source: detailForm.source }
            : {}),
        },
      },
      {
        onSuccess: (res) => {
          toast.success('Changes saved');
          const updated = res.data?.data as Lead | undefined;
          if (updated) {
            setDetailForm((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                status: (updated.status ?? prev.status) as LeadStatus,
                nextFollowUpLocal: toIstDatetimeLocalValue(updated.nextFollowUpAt),
                ageLocal: baselineLeadAgeInput(updated as LeadDetail),
                gender: (updated.gender ?? 'unset') as DetailFormState['gender'],
                profession: updated.profession ?? prev.profession ?? '',
                location: updated.location ?? prev.location ?? '',
                leadSubStatus: updated.leadSubStatus ?? prev.leadSubStatus,
                notes: updated.notes?.trim() ?? prev.notes,
                isSurgeryCase:
                  updated.isSurgeryCase === true
                    ? 'yes'
                    : updated.isSurgeryCase === false
                      ? 'no'
                      : 'unset',
                aestheticProcedure:
                  updated.aestheticProcedureName?.trim() ?? prev.aestheticProcedure,
                tentativeSurgeryDateLocal: updated.tentativeSurgeryDate
                  ? toIstDateInputValue(updated.tentativeSurgeryDate)
                  : '',
                alternatePhone: updated.alternatePhone ?? prev.alternatePhone,
                email: updated.email?.trim() ?? prev.email,
                contactPersonName: updated.contactPersonName ?? prev.contactPersonName,
                contactRelationship: updated.contactRelationship ?? prev.contactRelationship,
                source: normLeadSourceKey(updated.source) || prev.source,
                specialisation: updated.specialisation?.trim() ?? prev.specialisation,
              };
            });
          }
          const finalStatus = updated?.status ?? detailForm.status;
          const subAfter = (updated?.leadSubStatus ?? detailForm.leadSubStatus) as LeadSubStatus | null;
          const patientsPath = crmPatientsPathForOpdBooking(user?.role);
          if (
            finalStatus === 'CONVERTED' &&
            subAfter === 'OPD_BOOKED' &&
            !agencyViewOnly &&
            patientsPath
          ) {
            const navLead = (updated ?? leadDetail) as LeadDetail;
            if (updated) {
              setLeadDetail((prev) => (prev ? { ...prev, ...updated } : prev));
            }
            const rawAge = detailForm.ageLocal.trim();
            const parsedFormAge =
              rawAge === ''
                ? null
                : (() => {
                    const n = Number.parseInt(rawAge, 10);
                    return Number.isFinite(n) && n >= 0 ? n : null;
                  })();
            const digits = String(navLead.phone || '')
              .replace(/\D/g, '')
              .slice(-10);
            navigate(patientsPath, {
              state: {
                fromLeadConversion: true,
                crmLeadId: navLead.id,
                leadHospitalId: navLead.hospitalId,
                openOpdAppointmentAfterPatient: user?.role === 'HOSPITAL_ADMIN',
                patientName: navLead.patientName,
                phone: digits.length === 10 ? digits : navLead.phone,
                email: (navLead.email ?? detailForm.email.trim()) || null,
                leadSource: navLead.source,
                gender:
                  navLead.gender ??
                  (detailForm.gender !== 'unset' ? detailForm.gender : undefined),
                dob: navLead.dob,
                age: navLead.age ?? parsedFormAge ?? undefined,
              },
            });
            onOpenChange(false);
            setLeadDetail(null);
            setDetailForm(null);
            setSelectedLead(null);
          }
        },
        onError: (err: unknown) => {
          toast.error(getApiErrorMessage(err, 'Could not save changes'));
        },
      }
    );
  };

  useEffect(() => {
    if (!open) {
      setSelectedLead(null);
      setLeadDetail(null);
      setDetailForm(null);
      setEditingPatientName(false);
      setPatientNameDraft('');
      setDetailFetching(false);
      setActivitySummary('');
      setWaFreeformText('');
      setWaTemplateName('');
      setWaTemplateParamsText('');
      return;
    }
    if (anchorLead) {
      void openLeadDetail(anchorLead);
    }
  }, [open, anchorLead?.id]);

  return (
    <>
            <Dialog
        open={open && !!selectedLead}
        onOpenChange={(next) => {
          if (!next) {
            onOpenChange(false);
            setSelectedLead(null);
            setLeadDetail(null);
            setDetailForm(null);
            setDetailFetching(false);
          }
        }}
      >
        <DialogContent className="lead-detail-artis ld-dialog p-0 gap-0 overflow-hidden max-h-[92vh] flex flex-col border-0 bg-transparent shadow-none sm:max-w-[1120px]">
          <DialogHeader className="sr-only">
            <DialogTitle>Lead details</DialogTitle>
            <DialogDescription>View and update this sales lead.</DialogDescription>
          </DialogHeader>
          {leadDetail ? (
            <>
              <header className="ld-header">
                <div className="ld-header-top">
                  <p className="ld-breadcrumb">
                    Leads / <strong>{leadDetail.patientName}</strong>
                  </p>
                  <span className="ld-brand">True Artis</span>
                </div>
                <div className="min-w-0">
                    {editingPatientName && !agencyViewOnly ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <Input
                          value={patientNameDraft}
                          onChange={(e) => setPatientNameDraft(e.target.value)}
                          className="max-w-md h-11 text-slate-900 bg-white/95 border-white/20 ring-1 ring-white/20 focus-visible:ring-2 focus-visible:ring-white/40"
                          placeholder="Patient name"
                          autoFocus
                          disabled={updateLeadMut.isPending}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const t = normalizePersonName(patientNameDraft);
                              if (!t) {
                                toast.error('Name cannot be empty');
                                return;
                              }
                              if (t === leadDetail.patientName) {
                                setEditingPatientName(false);
                                return;
                              }
                              updateLeadMut.mutate(
                                { id: leadDetail.id, data: { patientName: t } },
                                {
                                  onSuccess: () => {
                                    toast.success('Name updated');
                                    setEditingPatientName(false);
                                  },
                                  onError: (e: Error) => {
                                    const msg = (e as { response?: { data?: { message?: string } } })
                                      ?.response?.data?.message;
                                    toast.error(msg || 'Could not update name');
                                  },
                                }
                              );
                            }
                            if (e.key === 'Escape') {
                              setEditingPatientName(false);
                              setPatientNameDraft(leadDetail.patientName);
                            }
                          }}
                        />
                        <Button
                          type="button"
                          size="icon"
                          variant="secondary"
                          className="h-10 w-10 shrink-0 rounded-xl bg-white/15 text-white hover:bg-white/20 border border-white/15 shadow-sm backdrop-blur"
                          disabled={updateLeadMut.isPending}
                          onClick={() => {
                            const t = normalizePersonName(patientNameDraft);
                            if (!t) {
                              toast.error('Name cannot be empty');
                              return;
                            }
                            if (t === leadDetail.patientName) {
                              setEditingPatientName(false);
                              return;
                            }
                            updateLeadMut.mutate(
                              { id: leadDetail.id, data: { patientName: t } },
                              {
                                onSuccess: () => {
                                  toast.success('Name updated');
                                  setEditingPatientName(false);
                                },
                                onError: (e: Error) => {
                                  const msg = (e as { response?: { data?: { message?: string } } })
                                    ?.response?.data?.message;
                                  toast.error(msg || 'Could not update name');
                                },
                              }
                            );
                          }}
                          title="Save name"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-10 w-10 shrink-0 rounded-xl text-white hover:bg-white/15"
                          disabled={updateLeadMut.isPending}
                          onClick={() => {
                            setEditingPatientName(false);
                            setPatientNameDraft(leadDetail.patientName);
                          }}
                          title="Cancel"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 min-w-0">
                        <h2 className="ld-patient-name truncate">{leadDetail.patientName}</h2>
                        {!agencyViewOnly && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 shrink-0 rounded-xl text-white hover:bg-white/15"
                            title="Edit name"
                            onClick={() => {
                              setPatientNameDraft(leadDetail.patientName);
                              setEditingPatientName(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    )}
                    <div className="ld-meta-row">
                      <p className="font-mono">{formatLeadPhone(leadDetail.phone)}</p>
                      {leadDetail.email && (
                        <p className="truncate max-w-md opacity-90">{leadDetail.email}</p>
                      )}
                    </div>
                    {leadDetail.alternatePhone ? (
                      <p className="ld-meta-row text-sm font-mono opacity-90">
                        Additional: {formatLeadPhone(leadDetail.alternatePhone)}
                      </p>
                    ) : null}
                    <div className="flex flex-wrap gap-2 pt-1.5">
                      <span className="ld-chip">{leadDetail.source}</span>
                      {leadDetail.sourceDetails && (
                        <span className="ld-chip max-w-xs truncate" title={leadDetail.sourceDetails}>
                          {leadDetail.sourceDetails}
                        </span>
                      )}
                      {editingCampaignName && elevatedLeadAdmin ? (
                        <div className="flex items-center gap-1 max-w-sm">
                          <Input
                            value={campaignNameDraft}
                            onChange={(e) => setCampaignNameDraft(e.target.value)}
                            className="h-8 text-xs bg-white/95 text-slate-900 border-white/40"
                            placeholder="Campaign name"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                if (!leadDetail || updateLeadMut.isPending) return;
                                updateLeadMut.mutate(
                                  {
                                    id: leadDetail.id,
                                    data: { campaignName: campaignNameDraft.trim() || null },
                                  },
                                  {
                                    onSuccess: () => {
                                      setEditingCampaignName(false);
                                      toast.success('Campaign name updated');
                                    },
                                    onError: (err: unknown) => {
                                      const ax = err as { response?: { data?: { message?: string } } };
                                      toast.error(ax.response?.data?.message || 'Could not update campaign');
                                    },
                                  }
                                );
                              }
                              if (e.key === 'Escape') {
                                setEditingCampaignName(false);
                              }
                            }}
                          />
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-white hover:bg-white/15"
                            disabled={updateLeadMut.isPending}
                            onClick={() => {
                              if (!leadDetail) return;
                              updateLeadMut.mutate(
                                {
                                  id: leadDetail.id,
                                  data: { campaignName: campaignNameDraft.trim() || null },
                                },
                                {
                                  onSuccess: () => {
                                    setEditingCampaignName(false);
                                    toast.success('Campaign name updated');
                                  },
                                  onError: (err: unknown) => {
                                    const ax = err as { response?: { data?: { message?: string } } };
                                    toast.error(ax.response?.data?.message || 'Could not update campaign');
                                  },
                                }
                              );
                            }}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-white hover:bg-white/15"
                            onClick={() => setEditingCampaignName(false)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : leadCampaignLabel(leadDetail) || elevatedLeadAdmin ? (
                        <span className={cn('ld-chip ld-chip--dashed max-w-xs', !leadCampaignLabel(leadDetail) && 'opacity-90')}>
                          <span className="truncate" title={leadCampaignLabel(leadDetail) || undefined}>
                            {leadCampaignLabel(leadDetail) || 'No campaign'}
                          </span>
                          {elevatedLeadAdmin && !agencyViewOnly && (
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-5 w-5 shrink-0 text-white/90 hover:bg-white/15"
                              title="Edit campaign name"
                              onClick={() => {
                                setCampaignNameDraft(leadDetail.campaignName?.trim() || '');
                                setEditingCampaignName(true);
                              }}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          )}
                        </span>
                      ) : null}
                      <span className="ld-chip ld-chip--status">
                        {formatLeadStatusLabel(detailForm?.status ?? leadDetail.status)}
                      </span>
                    </div>
                </div>
                <div className="ld-header-actions">
                  {selectedLead && open && navLeads.length > 1 ? (
                    <>
                      <button
                        type="button"
                        className="ld-nav-btn ld-motion"
                        disabled={detailFetching || detailNavIndex <= 0}
                        title="Previous lead on this page"
                        aria-label="Previous lead on this page"
                        onClick={() => goAdjacentLead(-1)}
                      >
                        <ChevronLeft className="h-4 w-4" aria-hidden />
                      </button>
                      <span className="ld-nav-count tabular-nums">
                        {detailFetching ? 'Loading… ' : null}
                        {detailNavIndex >= 0 ? (
                          <>
                            {detailNavIndex + 1} / {navLeads.length}
                            <span className="opacity-70"> · this page</span>
                          </>
                        ) : (
                          <span className="opacity-70">Not in current list</span>
                        )}
                      </span>
                      <button
                        type="button"
                        className="ld-nav-btn ld-motion"
                        disabled={
                          detailFetching ||
                          detailNavIndex < 0 ||
                          detailNavIndex >= navLeads.length - 1
                        }
                        title="Next lead on this page"
                        aria-label="Next lead on this page"
                        onClick={() => goAdjacentLead(1)}
                      >
                        <ChevronRight className="h-4 w-4" aria-hidden />
                      </button>
                    </>
                  ) : null}
                  {elevatedLeadAdmin && !agencyViewOnly ? (
                    deleteConfirmOpen ? (
                      <div className="rounded-md bg-white p-3 text-[var(--ld-ink)] shadow-lg max-w-[220px]">
                        <p className="text-xs font-medium">Delete this lead permanently?</p>
                        <p className="mt-1 text-[11px] text-[var(--ld-ink-muted)]">This cannot be undone.</p>
                        <div className="mt-3 flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            className="h-8"
                            disabled={deleteLeadMut.isPending}
                            onClick={() => deleteLeadMut.mutate(leadDetail.id)}
                          >
                            {deleteLeadMut.isPending ? 'Deleting…' : 'Delete'}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8"
                            disabled={deleteLeadMut.isPending}
                            onClick={() => setDeleteConfirmOpen(false)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="ld-delete-btn ld-motion"
                        title="Delete lead"
                        onClick={() => setDeleteConfirmOpen(true)}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                        Delete
                      </button>
                    )
                  ) : null}
                </div>
              </header>

              <div className="ld-body-scroll flex-1 min-h-0 text-sm">
                <div className="ld-columns">
                  <div className="ld-main">
                {(leadDetail.aiExplanation || leadDetail.aiConfidence != null) && (
                  <div className="ld-ai-banner ld-motion">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-semibold text-[var(--ld-ink)]">AI insight</p>
                      {leadDetail.aiConfidence != null && (
                        <p className="text-xs font-medium text-[var(--ld-ink-muted)]">
                          Confidence: {leadDetail.aiConfidence}%
                        </p>
                      )}
                    </div>
                    {leadDetail.aiExplanation && (
                      <p className="mt-2 text-sm text-[var(--ld-ink-soft)] leading-relaxed">
                        {leadDetail.aiExplanation}
                      </p>
                    )}
                  </div>
                )}

                {detailForm && (
                  <>
                    <LeadDetailCard title="Patient details">
                      <div className="ld-field-grid ld-field-grid--2">
                        <div>
                          <LeadDetailFieldLabel>Age</LeadDetailFieldLabel>
                          {agencyViewOnly ? (
                            <p className="mt-1 text-sm text-slate-800">
                              {leadDetail.age != null && Number.isFinite(leadDetail.age)
                                ? String(leadDetail.age)
                                : approximateAgeFromDob(leadDetail.dob ?? null) ?? '—'}
                            </p>
                          ) : (
                            <Input
                              type="number"
                              min={0}
                              max={125}
                              value={detailForm.ageLocal}
                              onChange={(e) =>
                                setDetailForm((f) => (f ? { ...f, ageLocal: e.target.value } : f))
                              }
                              placeholder="Years"
                              className={LD_INPUT}
                            />
                          )}
                        </div>
                        <div>
                          <LeadDetailFieldLabel>Gender</LeadDetailFieldLabel>
                          {agencyViewOnly ? (
                            <p className="mt-1 text-sm text-slate-800">
                              {leadDetail.gender
                                ? leadDetail.gender === 'MALE'
                                  ? 'Male'
                                  : leadDetail.gender === 'FEMALE'
                                    ? 'Female'
                                    : 'Other'
                                : '—'}
                            </p>
                          ) : (
                            <Select
                              value={detailForm.gender}
                              onValueChange={(v) =>
                                setDetailForm((f) =>
                                  f ? { ...f, gender: v as DetailFormState['gender'] } : f
                                )
                              }
                            >
                              <SelectTrigger className={LD_SELECT_TRIGGER}>
                                <SelectValue placeholder="Not specified" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unset">Not specified</SelectItem>
                                <SelectItem value="MALE">Male</SelectItem>
                                <SelectItem value="FEMALE">Female</SelectItem>
                                <SelectItem value="OTHER">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        <div className="sm:col-span-2">
                          <LeadDetailFieldLabel>Email</LeadDetailFieldLabel>
                          {agencyViewOnly ? (
                            <p className="mt-1 text-sm text-slate-800 break-all">
                              {leadDetail.email?.trim() || '—'}
                            </p>
                          ) : (
                            <Input
                              type="email"
                              value={detailForm.email}
                              onChange={(e) =>
                                setDetailForm((f) => (f ? { ...f, email: e.target.value } : f))
                              }
                              placeholder="patient@example.com"
                              className={LD_INPUT}
                              autoComplete="email"
                            />
                          )}
                        </div>
                        <div className="sm:col-span-2">
                          <LeadDetailFieldLabel>Additional phone</LeadDetailFieldLabel>
                          {agencyViewOnly ? (
                            <p className="text-sm font-mono text-slate-800 mt-1">
                              {leadDetail.alternatePhone?.trim() || '—'}
                            </p>
                          ) : (
                            <Input
                              value={detailForm.alternatePhone}
                              onChange={(e) =>
                                setDetailForm((f) =>
                                  f ? { ...f, alternatePhone: e.target.value } : f
                                )
                              }
                              placeholder="Alternate mobile"
                              className={cn(LD_INPUT, 'font-mono')}
                              inputMode="tel"
                              autoComplete="off"
                            />
                          )}
                        </div>
                        <div>
                          <LeadDetailFieldLabel>Profession</LeadDetailFieldLabel>
                          {agencyViewOnly ? (
                            <p className="mt-1 text-sm text-slate-800">
                              {leadDetail.profession?.trim() || '—'}
                            </p>
                          ) : (
                            <Input
                              value={detailForm.profession}
                              onChange={(e) =>
                                setDetailForm((f) =>
                                  f ? { ...f, profession: e.target.value } : f
                                )
                              }
                              placeholder="e.g. Business, Student, IT, Teacher…"
                              className={LD_INPUT}
                              autoComplete="off"
                            />
                          )}
                        </div>
                        <div>
                          <LeadDetailFieldLabel>Location</LeadDetailFieldLabel>
                          {agencyViewOnly ? (
                            <p className="mt-1 text-sm text-slate-800">
                              {leadDetail.location?.trim() || '—'}
                            </p>
                          ) : (
                            <Input
                              value={detailForm.location}
                              onChange={(e) =>
                                setDetailForm((f) =>
                                  f ? { ...f, location: e.target.value } : f
                                )
                              }
                              placeholder="e.g. Baner, Pune"
                              className={LD_INPUT}
                              autoComplete="off"
                            />
                          )}
                        </div>
                        <div className="sm:col-span-2">
                          <LeadDetailFieldLabel>Specialisation</LeadDetailFieldLabel>
                          {agencyViewOnly ? (
                            <p className="mt-1 text-sm text-slate-800">
                              {leadDetail.specialisation?.trim() || '—'}
                            </p>
                          ) : (
                            <Select
                              value={
                                detailForm.specialisation.trim()
                                  ? detailForm.specialisation
                                  : '__none__'
                              }
                              onValueChange={(v) =>
                                setDetailForm((f) =>
                                  f
                                    ? {
                                        ...f,
                                        specialisation: v === '__none__' ? '' : v,
                                      }
                                    : f
                                )
                              }
                            >
                              <SelectTrigger className={LD_SELECT_TRIGGER}>
                                <SelectValue placeholder="Select specialisation" />
                              </SelectTrigger>
                              <SelectContent className="max-h-72">
                                <SelectItem value="__none__">Not specified</SelectItem>
                                {specialisationDropdownOptions.map((s) => (
                                  <SelectItem key={s} value={s}>
                                    {s}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                    </LeadDetailCard>

                    <LeadDetailCard title="Contact & surgery">
                      <div className="ld-field-grid ld-field-grid--2">
                        <div>
                          <LeadDetailFieldLabel>Contact person name</LeadDetailFieldLabel>
                          {agencyViewOnly ? (
                            <p className="text-sm text-slate-800 mt-1">
                              {leadDetail.contactPersonName?.trim() || '—'}
                            </p>
                          ) : (
                            <Input
                              value={detailForm.contactPersonName}
                              onChange={(e) =>
                                setDetailForm((f) =>
                                  f ? { ...f, contactPersonName: e.target.value } : f
                                )
                              }
                              placeholder="e.g. name of spouse / family member"
                              className={LD_INPUT}
                              autoComplete="off"
                            />
                          )}
                        </div>
                        <div>
                          <LeadDetailFieldLabel>Relationship</LeadDetailFieldLabel>
                          {agencyViewOnly ? (
                            <p className="text-sm text-slate-800 mt-1">
                              {leadDetail.contactRelationship?.trim() || '—'}
                            </p>
                          ) : (
                            <Select
                              value={detailForm.contactRelationship.trim() ? detailForm.contactRelationship : '__none__'}
                              onValueChange={(v) =>
                                setDetailForm((f) =>
                                  f
                                    ? {
                                        ...f,
                                        contactRelationship: v === '__none__' ? '' : v,
                                      }
                                    : f
                                )
                              }
                            >
                              <SelectTrigger className={LD_SELECT_TRIGGER}>
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent className="max-h-56">
                                <SelectItem value="__none__">Not specified</SelectItem>
                                {contactRelationshipDropdownOptions.map((r) => (
                                  <SelectItem key={r} value={r}>
                                    {r}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                      <div className="mt-4 space-y-4">
                        <div>
                          <LeadDetailFieldLabel>Surgery case?</LeadDetailFieldLabel>
                          {agencyViewOnly ? (
                            <p className="mt-2 text-sm font-medium text-slate-800">
                              {leadDetail.isSurgeryCase === true
                                ? 'Yes'
                                : leadDetail.isSurgeryCase === false
                                  ? 'No'
                                  : 'Not specified'}
                            </p>
                          ) : (
                            <Select
                              value={detailForm.isSurgeryCase}
                              onValueChange={(v) =>
                                setDetailForm((f) =>
                                  f
                                    ? {
                                        ...f,
                                        isSurgeryCase: v as DetailFormState['isSurgeryCase'],
                                        tentativeSurgeryDateLocal:
                                          v !== 'yes' ? '' : f.tentativeSurgeryDateLocal,
                                        aestheticProcedure: v !== 'yes' ? '' : f.aestheticProcedure,
                                      }
                                    : f
                                )
                              }
                            >
                              <SelectTrigger className={LD_SELECT_TRIGGER}>
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unset">Not specified</SelectItem>
                                <SelectItem value="yes">Yes</SelectItem>
                                <SelectItem value="no">No</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        <div>
                          <LeadDetailFieldLabel>Surgery / procedure name</LeadDetailFieldLabel>
                          {agencyViewOnly ? (
                            <p className="mt-1 text-sm text-slate-800">
                              {leadDetail.isSurgeryCase === true &&
                              leadDetail.aestheticProcedureName?.trim()
                                ? leadDetail.aestheticProcedureName.trim()
                                : '—'}
                            </p>
                          ) : detailForm.isSurgeryCase === 'yes' ? (
                            <Select
                              value={
                                detailForm.aestheticProcedure.trim()
                                  ? detailForm.aestheticProcedure
                                  : '__none__'
                              }
                              onValueChange={(v) =>
                                setDetailForm((f) =>
                                  f
                                    ? {
                                        ...f,
                                        aestheticProcedure:
                                          v === '__none__' ? '' : v,
                                      }
                                    : f
                                )
                              }
                            >
                              <SelectTrigger className={LD_SELECT_TRIGGER}>
                                <SelectValue placeholder="Select procedure" />
                              </SelectTrigger>
                              <SelectContent className="max-h-52">
                                <SelectItem value="__none__">Not specified</SelectItem>
                                {aestheticProcedureDropdownOptions.map((proc) => (
                                  <SelectItem key={proc} value={proc}>
                                    {proc}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <p className="mt-2 text-sm text-slate-500">—</p>
                          )}
                        </div>
                      </div>
                    </LeadDetailCard>
                  </>
                )}

                <LeadDetailCard title="Pipeline status & notes">
                    {agencyViewOnly ? (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-slate-800">
                          {formatLeadStatusLabel(leadDetail.status)}
                        </p>
                        <p className="text-xs text-slate-500 mt-3 uppercase tracking-wide">
                          Sub-status
                        </p>
                        <p className="text-sm text-slate-800 mt-1">
                          {formatLeadSubStatusLabel(leadDetail.leadSubStatus)}
                        </p>
                        {leadDetail.status === 'CONVERTED' && isOpdVisitedOrAbove(leadDetail.leadSubStatus) ? (
                          <div className="mt-3">
                            <p className="text-xs text-slate-500 mt-3 uppercase tracking-wide">
                              OPD visited date
                            </p>
                            <p className="text-sm text-slate-800 mt-1">
                              {formatIstDateLabel(leadDetail.opdVisitedAt ?? null)}
                            </p>
                          </div>
                        ) : null}
                        {leadDetail.notes?.trim() ? (
                          <div className="mt-3">
                            <p className="text-xs text-slate-500 uppercase tracking-wide">Notes</p>
                            <p className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">
                              {leadDetail.notes}
                            </p>
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      detailForm && (
                        <>
                          <select
                            value={detailForm.status}
                            onChange={(e) =>
                              setDetailForm((f) =>
                                f
                                  ? {
                                      ...f,
                                      status: e.target.value as LeadStatus,
                                      // Reset sub-status because available options change with status.
                                      leadSubStatus: null,
                                    }
                                  : f
                              )
                            }
                            className={cn(LD_NATIVE_SELECT, 'mt-0')}
                          >
                            {LEAD_STATUS_OPTIONS.map((s) => (
                              <option key={s} value={s}>
                                {formatLeadStatusLabel(s)}
                              </option>
                            ))}
                          </select>
                          <div className="mt-3">
                            <LeadDetailFieldLabel>Sub-status</LeadDetailFieldLabel>
                            <Select
                              value={detailForm.leadSubStatus ?? '__none__'}
                              onValueChange={(v) => {
                                setDetailForm((f) =>
                                  f
                                    ? {
                                        ...f,
                                        ...(v === 'WRONG_NUMBER' ? { status: 'LOST' as const } : {}),
                                        leadSubStatus:
                                          v === '__none__' ? null : (v as LeadSubStatus),
                                      }
                                    : f
                                );
                              }}
                            >
                              <SelectTrigger className={LD_SELECT_TRIGGER}>
                                <SelectValue placeholder="Not specified" />
                              </SelectTrigger>
                              <SelectContent className="max-h-60">
                                <SelectItem value="__none__">Not specified</SelectItem>
                                {LEAD_SUBSTATUS_OPTIONS_BY_STATUS[detailForm.status]?.map((ss) => (
                                  <SelectItem key={ss} value={ss}>
                                    {formatLeadSubStatusLabel(ss)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {detailForm.status === 'CONVERTED' && isOpdVisitedOrAbove(detailForm.leadSubStatus) ? (
                            <div className="mt-3">
                              <LeadDetailFieldLabel>OPD visited date</LeadDetailFieldLabel>
                              <p className="mt-2 text-sm text-slate-800">
                                {formatIstDateLabel(leadDetail.opdVisitedAt ?? null)}
                              </p>
                            </div>
                          ) : null}
                          <div className="mt-3">
                            <LeadDetailFieldLabel>
                              Notes
                              {detailForm.status === 'LOST' && wasEverOpdVisited(leadDetail) ? (
                                <span className="text-[var(--ld-coral)] normal-case tracking-normal">
                                  {' '}
                                  (required — reason for Lost)
                                </span>
                              ) : null}
                            </LeadDetailFieldLabel>
                            <textarea
                              value={detailForm.notes}
                              onChange={(e) =>
                                setDetailForm((f) => (f ? { ...f, notes: e.target.value } : f))
                              }
                              rows={3}
                              placeholder={
                                detailForm.status === 'LOST' && wasEverOpdVisited(leadDetail)
                                  ? 'Enter why this OPD visited case was lost…'
                                  : 'Optional notes'
                              }
                              className={cn(LD_TEXTAREA, 'mt-0 min-h-[5rem]')}
                            />
                          </div>
                        </>
                      )
                    )}
                </LeadDetailCard>

                {leadDetail && (
                  <LeadBantScoringCard
                    leadId={leadDetail.id}
                    aestheticProcedureName={leadDetail.aestheticProcedureName}
                    procedureInterest={leadDetail.procedureInterest}
                  />
                )}

                {leadDetail?.lostRevivalInboundAt && (
                  <div className="ld-alert ld-alert--sage">
                    <p className="ld-field-label mb-1">Lost revival — customer replied</p>
                    <p className="text-sm">
                      Source set to <strong>{leadDetail.source}</strong>. Customer responded at{' '}
                      {new Date(leadDetail.lostRevivalInboundAt).toLocaleString('en-IN', {
                        timeZone: 'Asia/Kolkata',
                      })}{' '}
                      IST — call immediately.
                    </p>
                  </div>
                )}

                {leadDetail?.callNowRequestedAt && (
                  <div className="ld-alert ld-alert--coral">
                    <p className="ld-field-label mb-1">Call now — customer waiting</p>
                    <p className="text-sm">
                      Customer tapped <strong>Call Now Please</strong> at{' '}
                      {new Date(leadDetail.callNowRequestedAt).toLocaleString('en-IN', {
                        timeZone: 'Asia/Kolkata',
                      })}{' '}
                      IST. Call immediately.
                    </p>
                  </div>
                )}

                {isActiveCustomerCallbackAt(
                  leadDetail?.customerCallbackAt,
                  leadDetail?.nextFollowUpAt
                ) &&
                  leadDetail?.customerCallbackAt && (
                  <div className="ld-alert ld-alert--azure">
                    <p className="ld-field-label mb-1">Customer requested callback</p>
                    <p className="mt-1 text-lg font-semibold">
                      {new Date(leadDetail.customerCallbackAt).toLocaleString('en-IN', {
                        timeZone: 'Asia/Kolkata',
                      })}{' '}
                      IST
                    </p>
                    <p className="mt-1 text-sm opacity-90">
                      Lead is marked for follow-up — call at the time above.
                    </p>
                  </div>
                )}

                {leadDetail?.campaignWelcomeWaSent && leadDetail.campaignWelcomeWaSentAt && (
                  <div className="ld-alert ld-alert--gold">
                    <p className="ld-field-label mb-1">Campaign welcome WhatsApp sent</p>
                    <p className="text-sm">
                      Auto welcome message sent at{' '}
                      {new Date(leadDetail.campaignWelcomeWaSentAt).toLocaleString('en-IN', {
                        timeZone: 'Asia/Kolkata',
                      })}{' '}
                      IST (template: true_artis_campaign_welcome).
                    </p>
                  </div>
                )}

                {leadDetail?.websiteOpdAppointmentId && (
                  <div className="ld-alert ld-alert--sage">
                    <p className="ld-field-label mb-1">Website OPD booked — follow up</p>
                    <div className="mt-2 space-y-1 text-sm">
                      {leadDetail.websiteOpdDoctorName && (
                        <p>
                          <span className="font-medium">Doctor:</span> {leadDetail.websiteOpdDoctorName}
                        </p>
                      )}
                      {leadDetail.websiteOpdHospitalName && (
                        <p>
                          <span className="font-medium">Centre:</span> {leadDetail.websiteOpdHospitalName}
                        </p>
                      )}
                      {leadDetail.websiteOpdScheduledAt && (
                        <p>
                          <span className="font-medium">Appointment:</span>{' '}
                          {new Date(leadDetail.websiteOpdScheduledAt).toLocaleString('en-IN', {
                            timeZone: 'Asia/Kolkata',
                            weekday: 'long',
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}{' '}
                          IST
                        </p>
                      )}
                      {leadDetail.websiteOpdBookedAt && (
                        <p className="opacity-90">
                          Booked via website at{' '}
                          {new Date(leadDetail.websiteOpdBookedAt).toLocaleString('en-IN', {
                            timeZone: 'Asia/Kolkata',
                          })}{' '}
                          IST
                        </p>
                      )}
                      <p className="text-xs opacity-80 font-mono">
                        Appt ID: {leadDetail.websiteOpdAppointmentId}
                      </p>
                    </div>
                    <p className="mt-2 text-sm">
                      Lead status is <strong>Converted → OPD Booked</strong>. Confirm attendance and
                      collect consultation fee at visit.
                    </p>
                  </div>
                )}

              <LeadDetailCard title="WhatsApp thread">
                <p className="ld-muted text-xs mb-3">This phone — all lead records</p>
                {leadDetail.whatsappMeta?.optedOut ? (
                  <p className="ld-alert ld-alert--coral mb-3">
                    This number has opted out of WhatsApp — do not send marketing messages via Meta until they message you
                    again or provide consent outside this channel.
                  </p>
                ) : null}
                {leadDetail.whatsappMessages && leadDetail.whatsappMessages.length > 0 ? (
                  <div className="ld-wa-thread mb-4">
                    {leadDetail.whatsappMessages.map((m) => {
                      const when = new Date(m.createdAt).toLocaleString('en-IN', {
                        timeZone: 'Asia/Kolkata',
                      });
                      const outbound = m.direction === 'OUTBOUND';
                      return (
                        <div
                          key={m.id}
                          className={outbound ? 'ld-wa-bubble-out' : 'ld-wa-bubble-in'}
                        >
                          <div className="ld-wa-meta">
                            {outbound ? 'Out' : 'In'}
                            {m.createdBy?.name ? ` · ${m.createdBy.name}` : ''} · {when}
                          </div>
                          <div className="whitespace-pre-wrap break-words">{m.body}</div>
                          {m.attachmentUrl ? (
                            <div className="mt-2">
                              {m.attachmentMime?.startsWith('image/') ? (
                                <a
                                  href={m.attachmentUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={`inline-block rounded-lg overflow-hidden ring-1 ${
                                    outbound ? 'ring-white/30' : 'ring-slate-200/70'
                                  }`}
                                  title={m.attachmentFilename || 'Open image'}
                                >
                                  <img
                                    src={m.attachmentUrl}
                                    alt={m.attachmentFilename || 'WhatsApp image'}
                                    className="h-24 w-auto max-w-[220px] object-cover bg-black/5"
                                    loading="lazy"
                                  />
                                </a>
                              ) : (
                                <a
                                  href={m.attachmentUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium ring-1 ${
                                    outbound
                                      ? 'bg-white/10 text-white ring-white/20 hover:bg-white/15'
                                      : 'bg-white text-slate-800 ring-slate-200/70 hover:bg-slate-50'
                                  }`}
                                >
                                  <span className="truncate max-w-[220px]">
                                    {m.attachmentFilename || 'Download file'}
                                  </span>
                                </a>
                              )}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="mb-4" />
                )}
                {leadDetail.whatsappMeta &&
                !leadDetail.whatsappMeta.optedOut &&
                leadDetail.whatsappMeta.freeformUntil &&
                leadDetail.whatsappMeta.canUseFreeform ? (
                  <p className="ld-muted text-[11px] text-center mb-2">
                    Free-form replies allowed until{' '}
                    {new Date(leadDetail.whatsappMeta.freeformUntil).toLocaleString('en-IN', {
                      timeZone: 'Asia/Kolkata',
                    })}{' '}
                    IST (24h from last inbound).
                  </p>
                ) : null}
                {!agencyViewOnly && leadDetail.whatsappMeta && !leadDetail.whatsappMeta.optedOut ? (
                  <div className="space-y-3">
                    {leadDetail.whatsappMeta.canUseFreeform ? (
                      <>
                        <Textarea
                          value={waFreeformText}
                          onChange={(e) => setWaFreeformText(e.target.value)}
                          placeholder="Type your WhatsApp reply…"
                          rows={3}
                          className={cn(LD_TEXTAREA, 'resize-none')}
                        />
                        <button
                          type="button"
                          disabled={sendLeadWhatsAppMut.isPending || !waFreeformText.trim()}
                          className="ld-btn-sage ld-motion mt-2"
                          onClick={() => {
                            if (!leadDetail) return;
                            sendLeadWhatsAppMut.mutate({
                              leadId: leadDetail.id,
                              text: waFreeformText.trim(),
                            });
                          }}
                        >
                          {sendLeadWhatsAppMut.isPending ? 'Sending…' : 'Send on WhatsApp'}
                        </button>

                        <div className="pt-3 mt-3 border-t border-[var(--ld-line)]">
                          <LeadDetailFieldLabel>Send image / PDF / document (within 24h window)</LeadDetailFieldLabel>
                          <div className="mt-2 flex flex-col gap-2">
                            <Input
                              type="file"
                              accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain"
                              onChange={(e) => setWaMediaFile(e.target.files?.[0] || null)}
                              className={LD_INPUT}
                            />
                            <Input
                              value={waMediaCaption}
                              onChange={(e) => setWaMediaCaption(e.target.value)}
                              placeholder="Caption (optional)"
                              className={LD_INPUT}
                            />
                            <button
                              type="button"
                              disabled={sendLeadWhatsAppMediaMut.isPending || !waMediaFile}
                              className="ld-btn-sage ld-motion"
                              onClick={() => {
                                if (!leadDetail || !waMediaFile) return;
                                sendLeadWhatsAppMediaMut.mutate({
                                  leadId: leadDetail.id,
                                  file: waMediaFile,
                                  caption: waMediaCaption.trim() || undefined,
                                });
                              }}
                            >
                              {sendLeadWhatsAppMediaMut.isPending ? 'Sending…' : 'Send file on WhatsApp'}
                            </button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="ld-muted text-xs leading-snug">
                          Outside the 24-hour window you must use an{' '}
                          <strong className="font-semibold text-[var(--ld-ink)]">approved template</strong> from Meta Business
                          Manager. Enter the template name exactly as in Meta (and one body variable per line, in order).
                        </p>
                        <Input
                          value={waTemplateName}
                          onChange={(e) => setWaTemplateName(e.target.value)}
                          placeholder="Template name (e.g. hello_world)"
                          className={LD_INPUT}
                        />
                        <div className="flex gap-2 flex-wrap items-center">
                          <LeadDetailFieldLabel className="mb-0 shrink-0">Language code</LeadDetailFieldLabel>
                          <Input
                            value={waTemplateLanguage}
                            onChange={(e) => setWaTemplateLanguage(e.target.value)}
                            placeholder="en"
                            className={cn(LD_INPUT, 'w-24')}
                          />
                        </div>
                        <Textarea
                          value={waTemplateParamsText}
                          onChange={(e) => setWaTemplateParamsText(e.target.value)}
                          placeholder="Body variables — one per line ({{1}}, {{2}}, …)"
                          rows={4}
                          className={cn(LD_TEXTAREA, 'resize-none font-mono text-sm')}
                        />
                        <button
                          type="button"
                          disabled={sendLeadWhatsAppMut.isPending || !waTemplateName.trim()}
                          className="ld-btn-sage ld-motion"
                          onClick={() => {
                            if (!leadDetail) return;
                            const params = waTemplateParamsText
                              .split(/\r?\n/)
                              .map((line) => line.trim());
                            sendLeadWhatsAppMut.mutate({
                              leadId: leadDetail.id,
                              templateName: waTemplateName.trim(),
                              templateLanguage: waTemplateLanguage.trim() || 'en',
                              templateBodyParams: params,
                            });
                          }}
                        >
                          {sendLeadWhatsAppMut.isPending ? 'Sending…' : 'Send template'}
                        </button>

                        <div className="pt-3 mt-3 border-t border-[var(--ld-line)]">
                          <LeadDetailFieldLabel>
                            Template header media (optional, recommended for PDFs/images outside 24h)
                          </LeadDetailFieldLabel>
                          <div className="mt-2 flex flex-col gap-2">
                            <Input
                              type="file"
                              accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain"
                              onChange={(e) => setWaTemplateHeaderFile(e.target.files?.[0] || null)}
                              className={LD_INPUT}
                            />
                            <button
                              type="button"
                              disabled={
                                sendLeadWhatsAppMediaMut.isPending ||
                                !waTemplateName.trim() ||
                                !waTemplateHeaderFile
                              }
                              className="ld-btn-sage ld-motion"
                              onClick={() => {
                                if (!leadDetail || !waTemplateHeaderFile) return;
                                const params = waTemplateParamsText
                                  .split(/\r?\n/)
                                  .map((line) => line.trim())
                                  .filter(Boolean);
                                sendLeadWhatsAppMediaMut.mutate({
                                  leadId: leadDetail.id,
                                  file: waTemplateHeaderFile,
                                  templateName: waTemplateName.trim(),
                                  templateLanguage: waTemplateLanguage.trim() || 'en',
                                  templateBodyParams: params,
                                });
                              }}
                            >
                              {sendLeadWhatsAppMediaMut.isPending ? 'Sending…' : 'Send template + header media'}
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ) : null}
              </LeadDetailCard>

              {leadDetail.appCallRecordings && leadDetail.appCallRecordings.length > 0 ? (
                <LeadDetailCard title="App call recordings">
                  <p className="ld-muted text-xs mb-3">
                    SetuOS Sales app — outbound and inbound CRM calls synced from the linked dialer
                    recording folder (Android).
                  </p>
                  <ul className="space-y-3 text-xs">
                    {leadDetail.appCallRecordings.map((rec) => (
                      <li
                        key={rec.id}
                        className="rounded-2xl bg-white/80 p-3 shadow-sm ring-1 ring-slate-200/40"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-semibold text-slate-900">
                            {new Date(rec.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                          </span>
                          {rec.durationSec != null ? (
                            <span className="text-slate-500 tabular-nums">{rec.durationSec}s</span>
                          ) : null}
                        </div>
                        {rec.staff?.name ? (
                          <p className="text-slate-600 mt-1">by {rec.staff.name}</p>
                        ) : null}
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <LeadActivityAudioPlayer
                            activityId={rec.activityId ?? undefined}
                            recordingId={rec.id}
                            label="Play app call recording"
                            showControls
                          />
                          {canDeleteRecording && !agencyViewOnly ? (
                            recordingDeleteConfirmId === rec.id ? (
                              <div className="flex flex-wrap items-center gap-2 rounded-lg bg-red-50 px-2 py-1.5 ring-1 ring-red-200/80">
                                <span className="text-[11px] font-medium text-red-900">
                                  Delete permanently?
                                </span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="destructive"
                                  className="h-7 text-xs"
                                  disabled={deleteCallRecordingMut.isPending}
                                  onClick={() => deleteCallRecordingMut.mutate(rec.id)}
                                >
                                  {deleteCallRecordingMut.isPending ? 'Deleting…' : 'Delete'}
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  disabled={deleteCallRecordingMut.isPending}
                                  onClick={() => setRecordingDeleteConfirmId(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-8 gap-1 text-xs text-red-800 border-red-200/80 hover:bg-red-50"
                                onClick={() => setRecordingDeleteConfirmId(rec.id)}
                              >
                                <Trash2 className="h-3.5 w-3.5" aria-hidden />
                                Delete
                              </Button>
                            )
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                </LeadDetailCard>
              ) : null}

              <LeadDetailCard title="Full history">
                <p className="ld-muted text-xs mb-3">This phone — all lead records</p>
                {leadDetail.crmTimeline && leadDetail.crmTimeline.length > 0 ? (
                  <div className="ld-timeline">
                    <div className="ld-timeline-line" aria-hidden />
                    <div className="space-y-0 text-xs">
                      {leadDetail.crmTimeline.map((row, idx) => {
                        const cross =
                          row.leadId !== leadDetail.id ? (
                            <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full ring-1 ring-amber-200/60">
                              earlier cycle
                            </span>
                          ) : null;
                        const when = new Date(row.at).toLocaleString('en-IN', {
                          timeZone: 'Asia/Kolkata',
                        });
                        if (row.kind === 'STATUS') {
                          const from = row.fromStatus
                            ? formatLeadStatusLabel(row.fromStatus)
                            : '—';
                          const to = formatLeadStatusLabel(row.toStatus);
                          return (
                            <div key={`s-${idx}-${row.at}`} className="ld-timeline-item">
                              <span className="ld-timeline-dot ld-timeline-dot--sage" aria-hidden />
                              <div className="ld-timeline-entry">
                                <div className="flex items-start justify-between gap-2 flex-wrap">
                                  <span className="font-semibold text-[var(--ld-ink)]">
                                    Status · {from} → {to}
                                    {cross}
                                  </span>
                                  <span className="ld-muted shrink-0">{when}</span>
                                </div>
                                {(row.note || row.changedBy) && (
                                  <p className="ld-muted mt-1">
                                    {row.note ?? (row.changedBy ? `by ${row.changedBy.name}` : '')}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        }
                        if (row.kind === 'OWNER') {
                          const from = row.fromStaff?.name ?? 'Unassigned';
                          const to = row.toStaff?.name ?? 'Unassigned';
                          return (
                            <div key={`o-${idx}-${row.at}`} className="ld-timeline-item">
                              <span className="ld-timeline-dot" aria-hidden />
                              <div className="ld-timeline-entry">
                                <div className="flex items-start justify-between gap-2 flex-wrap">
                                  <span className="font-semibold text-[var(--ld-ink)]">
                                    Owner · {from} → {to}
                                    {cross}
                                  </span>
                                  <span className="ld-muted shrink-0">{when}</span>
                                </div>
                                {row.changedBy && (
                                  <p className="ld-muted mt-1">by {row.changedBy.name}</p>
                                )}
                              </div>
                            </div>
                          );
                        }
                        const dotClass =
                          row.activityType === 'CALL'
                            ? 'ld-timeline-dot ld-timeline-dot--azure'
                            : 'ld-timeline-dot';
                        return (
                          <div
                            key={`a-${idx}-${row.at}-${row.activityType}-${row.attachmentUrl ?? ''}`}
                            className="ld-timeline-item"
                          >
                            <span className={dotClass} aria-hidden />
                            <div className="ld-timeline-entry">
                              <div className="flex items-start justify-between gap-2 flex-wrap">
                                <span className="font-semibold text-[var(--ld-ink)]">
                                  {activityTypeDisplayLabel(row.activityType)}
                                  {cross}
                                </span>
                                <span className="ld-muted shrink-0">{when}</span>
                              </div>
                              {editingActivityId === row.id ? (
                                <div className="mt-3 space-y-2">
                                  <select
                                    value={editingActivityType}
                                    onChange={(e) => setEditingActivityType(e.target.value)}
                                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                                  >
                                    <option value="CALL">Call</option>
                                    <option value="WHATSAPP">WhatsApp</option>
                                    <option value="MEETING">Meeting</option>
                                    <option value="EMAIL">Email</option>
                                    <option value="NOTE">Note</option>
                                    <option value="OTHER">Other</option>
                                  </select>
                                  <Textarea
                                    value={editingActivitySummary}
                                    onChange={(e) => setEditingActivitySummary(e.target.value)}
                                    rows={3}
                                    placeholder="Enter message"
                                  />
                                  <div className="flex items-center justify-end gap-2">
                                    <Button
                                      type="button"
                                      size="icon"
                                      variant="secondary"
                                      className="h-8 w-8 rounded-lg"
                                      disabled={updateActivityMut.isPending}
                                      onClick={() =>
                                        updateActivityMut.mutate({
                                          activityId: row.id,
                                          data: {
                                            type: editingActivityType,
                                            summary: editingActivitySummary,
                                          },
                                        })
                                      }
                                      title="Save"
                                    >
                                      <Check className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      type="button"
                                      size="icon"
                                      variant="ghost"
                                      className="h-8 w-8 rounded-lg"
                                      disabled={updateActivityMut.isPending}
                                      onClick={() => {
                                        setEditingActivityId(null);
                                        setEditingActivitySummary('');
                                        setEditingActivityType('CALL');
                                      }}
                                      title="Cancel"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              ) : row.summary ? (
                                <div className="mt-2 text-sm">
                                  <ActivitySummaryWithLinks text={row.summary} />
                                </div>
                              ) : null}
                              {row.attachmentUrl && (
                                <TimelineAttachmentPreview
                                  url={row.attachmentUrl}
                                  filename={row.attachmentFilename}
                                  mime={row.attachmentMime}
                                  activityId={row.kind === 'ACTIVITY' ? row.id : undefined}
                                  recordingId={
                                    row.kind === 'ACTIVITY' &&
                                    'recordingId' in row &&
                                    typeof row.recordingId === 'string'
                                      ? row.recordingId
                                      : undefined
                                  }
                                />
                              )}
                              {row.createdBy && (
                                <p className="text-slate-600 mt-2">by {row.createdBy.name}</p>
                              )}
                              {isEditableManualHistoryEntry(row, user?.id, agencyViewOnly) &&
                              editingActivityId !== row.id ? (
                                <div className="mt-3 flex items-center justify-end gap-2">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 rounded-lg px-2 text-slate-600"
                                    onClick={() => startEditingHistoryEntry(row)}
                                  >
                                    <Pencil className="mr-1 h-3.5 w-3.5" />
                                    Edit
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 rounded-lg px-2 text-rose-600 hover:text-rose-700"
                                    disabled={deleteActivityMut.isPending}
                                    onClick={() => {
                                      if (!window.confirm('Delete this history entry?')) return;
                                      deleteActivityMut.mutate(row.id);
                                    }}
                                  >
                                    <Trash2 className="mr-1 h-3.5 w-3.5" />
                                    Delete
                                  </Button>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : leadDetail.activities && leadDetail.activities.length > 0 ? (
                  <div className="relative">
                    <div className="absolute left-2 top-1 bottom-1 w-px bg-slate-200/70" />
                    <div className="space-y-3 text-xs">
                      {leadDetail.activities.map((a, idx) => (
                        <div key={a.id} className="relative pl-7">
                          <span className="absolute left-2 top-3 h-2.5 w-2.5 rounded-full bg-slate-400 ring-4 ring-[#F8FAFC]" />
                          <div className="rounded-2xl bg-white/80 p-4 shadow-sm ring-1 ring-slate-200/40 transition duration-200 hover:shadow-md">
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                              <span className="font-semibold text-slate-900">
                                {activityTypeDisplayLabel(a.type)}
                                {idx === 0 ? null : null}
                              </span>
                              <span className="text-slate-500">
                                {new Date(a.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                              </span>
                            </div>
                            {editingActivityId === a.id ? (
                              <div className="mt-3 space-y-2">
                                <select
                                  value={editingActivityType}
                                  onChange={(e) => setEditingActivityType(e.target.value)}
                                  className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                                >
                                  <option value="CALL">Call</option>
                                  <option value="WHATSAPP">WhatsApp</option>
                                  <option value="MEETING">Meeting</option>
                                  <option value="EMAIL">Email</option>
                                  <option value="NOTE">Note</option>
                                  <option value="OTHER">Other</option>
                                </select>
                                <Textarea
                                  value={editingActivitySummary}
                                  onChange={(e) => setEditingActivitySummary(e.target.value)}
                                  rows={3}
                                  placeholder="Enter message"
                                />
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="secondary"
                                    className="h-8 w-8 rounded-lg"
                                    disabled={updateActivityMut.isPending}
                                    onClick={() =>
                                      updateActivityMut.mutate({
                                        activityId: a.id,
                                        data: {
                                          type: editingActivityType,
                                          summary: editingActivitySummary,
                                        },
                                      })
                                    }
                                    title="Save"
                                  >
                                    <Check className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8 rounded-lg"
                                    disabled={updateActivityMut.isPending}
                                    onClick={() => {
                                      setEditingActivityId(null);
                                      setEditingActivitySummary('');
                                      setEditingActivityType('CALL');
                                    }}
                                    title="Cancel"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ) : a.summary ? (
                              <div className="mt-2 text-sm">
                                <ActivitySummaryWithLinks text={a.summary} />
                              </div>
                            ) : null}
                            {a.attachmentUrl && (
                              <TimelineAttachmentPreview
                                url={a.attachmentUrl}
                                filename={a.attachmentFilename}
                                mime={a.attachmentMime}
                                activityId={a.id}
                              />
                            )}
                            {a.createdBy && (
                              <p className="text-slate-600 mt-2">by {a.createdBy.name}</p>
                            )}
                            {isEditableManualHistoryEntry(a, user?.id, agencyViewOnly) &&
                            editingActivityId !== a.id ? (
                              <div className="mt-3 flex items-center justify-end gap-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 rounded-lg px-2 text-slate-600"
                                  onClick={() => startEditingHistoryEntry(a)}
                                >
                                  <Pencil className="mr-1 h-3.5 w-3.5" />
                                  Edit
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 rounded-lg px-2 text-rose-600 hover:text-rose-700"
                                  disabled={deleteActivityMut.isPending}
                                  onClick={() => {
                                    if (!window.confirm('Delete this history entry?')) return;
                                    deleteActivityMut.mutate(a.id);
                                  }}
                                >
                                  <Trash2 className="mr-1 h-3.5 w-3.5" />
                                  Delete
                                </Button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="ld-muted">No history yet.</p>
                )}
              </LeadDetailCard>
                </div>

                <aside className="ld-aside">
                  {detailForm &&
                    !agencyViewOnly &&
                    leadDetail &&
                    showLeadSourceEditor(user?.role, leadDetail.source, detailForm.source) && (
                      <LeadDetailCard title="Lead source">
                        <p className="ld-muted mb-3">
                          {canAdminChangeLeadSource(user?.role)
                            ? 'Correct the acquisition source for this lead.'
                            : 'Exotel calls can be reclassified to the correct marketing channel.'}
                        </p>
                        <LeadSourceSearchableSelect
                          value={detailForm.source}
                          onValueChange={(v) =>
                            setDetailForm((f) => (f ? { ...f, source: v } : f))
                          }
                          options={leadSourceSelectOptions}
                          placeholder="Select source"
                          searchPlaceholder="Search lead source…"
                          triggerClassName={cn(LD_SELECT_TRIGGER, 'mt-0')}
                        />
                      </LeadDetailCard>
                    )}

                  <LeadDetailCard title="Next follow-up">
                    {agencyViewOnly ? (
                      <div className="space-y-3">
                        <p className="ld-readonly">
                          {leadDetail.nextFollowUpAt
                            ? new Date(leadDetail.nextFollowUpAt).toLocaleString('en-IN', {
                                timeZone: 'Asia/Kolkata',
                              })
                            : '—'}
                        </p>
                        <div>
                          <LeadDetailFieldLabel>Tentative surgery date</LeadDetailFieldLabel>
                          <p className="ld-readonly">
                            {leadDetail.isSurgeryCase === true && leadDetail.tentativeSurgeryDate
                              ? new Date(leadDetail.tentativeSurgeryDate).toLocaleDateString('en-IN', {
                                  timeZone: 'Asia/Kolkata',
                                })
                              : '—'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      detailForm && (
                        <div className="space-y-4">
                          <div>
                            <LeadDetailFieldLabel>
                              Date & time
                              {detailForm.status !== 'LOST' && detailForm.status !== 'CLOSED' ? (
                                <span className="text-[var(--ld-coral)] normal-case tracking-normal"> *</span>
                              ) : null}
                            </LeadDetailFieldLabel>
                            <p className="ld-muted text-[11px] mt-0.5 mb-1">
                              Must be after the current date and time.
                            </p>
                            <Input
                              type="datetime-local"
                              value={detailForm.nextFollowUpLocal}
                              onChange={(e) =>
                                setDetailForm((f) =>
                                  f ? { ...f, nextFollowUpLocal: e.target.value } : f
                                )
                              }
                              className={cn(
                                LD_INPUT,
                                detailForm.status !== 'LOST' &&
                                  detailForm.status !== 'CLOSED' &&
                                  !detailForm.nextFollowUpLocal.trim()
                                  ? 'border-[var(--ld-coral)]'
                                  : ''
                              )}
                            />
                          </div>
                          <div>
                            <LeadDetailFieldLabel>Tentative surgery date</LeadDetailFieldLabel>
                            {detailForm.isSurgeryCase === 'yes' ? (
                              <Input
                                type="date"
                                value={detailForm.tentativeSurgeryDateLocal}
                                onChange={(e) =>
                                  setDetailForm((f) =>
                                    f ? { ...f, tentativeSurgeryDateLocal: e.target.value } : f
                                  )
                                }
                                className={LD_INPUT}
                              />
                            ) : (
                              <p className="ld-readonly">—</p>
                            )}
                          </div>
                        </div>
                      )
                    )}
                  </LeadDetailCard>

                  {leadDetail && !agencyViewOnly && (
                    <div className="ld-cadence-card ld-motion space-y-3">
                      <div>
                        <p className="text-sm font-semibold text-[var(--ld-ink)]">
                          Not reachable — call cadence (IST)
                        </p>
                        <p className="ld-muted mt-1 leading-snug">
                          Policy: up to <strong>3</strong> logged calls per <strong>IST calendar day</strong>, for up to{' '}
                          <strong>3 days</strong> (<strong>9</strong> total). Use{' '}
                          <strong>Log not-reachable call attempt</strong> only when the customer does not answer. If you
                          speak with them, log <strong>Call</strong> or <strong>WhatsApp</strong> in Quick Activity
                          below — the counter resets to <strong>0/9</strong>. After the 9th not-reachable log, the lead
                          moves to <strong>Lost</strong> with <strong>Long-term follow-up</strong>.
                        </p>
                      </div>
                      <div className="flex flex-wrap items-end gap-6">
                        <div>
                          <LeadDetailFieldLabel>Total progress</LeadDetailFieldLabel>
                          <p className="ld-cadence-stat tabular-nums">
                            {leadDetail.notReachableAttemptTotal ?? 0}
                            <span>/9</span>
                          </p>
                        </div>
                        <div>
                          <LeadDetailFieldLabel>Today (IST)</LeadDetailFieldLabel>
                          <p className="ld-cadence-stat tabular-nums text-2xl">
                            {(() => {
                              const istTodayKey = new Intl.DateTimeFormat('en-CA', {
                                timeZone: 'Asia/Kolkata',
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                              }).format(new Date());
                              const key = leadDetail.notReachableAttemptDayKey ?? '';
                              const n =
                                key === istTodayKey ? (leadDetail.notReachableAttemptsToday ?? 0) : 0;
                              return (
                                <>
                                  {n}
                                  <span>/3</span>
                                </>
                              );
                            })()}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="ld-btn-coral ld-motion"
                        disabled={
                          incrementNotReachableCallMut.isPending ||
                          (leadDetail.notReachableAttemptTotal ?? 0) >= 9 ||
                          (() => {
                            const istTodayKey = new Intl.DateTimeFormat('en-CA', {
                              timeZone: 'Asia/Kolkata',
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                            }).format(new Date());
                            const key = leadDetail.notReachableAttemptDayKey ?? '';
                            const n =
                              key === istTodayKey ? (leadDetail.notReachableAttemptsToday ?? 0) : 0;
                            return n >= 3;
                          })()
                        }
                        onClick={() => {
                          if (!leadDetail) return;
                          incrementNotReachableCallMut.mutate(leadDetail.id);
                        }}
                      >
                        {incrementNotReachableCallMut.isPending ? 'Logging…' : 'Log not-reachable call attempt'}
                      </button>
                    </div>
                  )}

                  {!agencyViewOnly && detailForm && leadDetail && (
                    <button
                      type="button"
                      className="ld-btn-primary ld-motion"
                      disabled={
                        !isDetailDirty(leadDetail, detailForm, user?.role) || updateLeadMut.isPending
                      }
                      onClick={handleDetailSubmit}
                    >
                      {updateLeadMut.isPending ? 'Saving…' : 'Submit'}
                    </button>
                  )}

                  <LeadDetailCard title="Quick activity">
                    <input
                      ref={activityFileRef}
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/jpeg,image/png,image/webp,image/gif"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f || agencyViewOnly || !leadDetail) return;
                        uploadLeadAttachmentMut.mutate({
                          leadId: leadDetail.id,
                          file: f,
                          summary: activitySummary.trim() || undefined,
                        });
                      }}
                    />
                    <div className="flex flex-col sm:flex-row gap-3 flex-wrap items-stretch sm:items-center">
                      <select
                        value={activityType}
                        onChange={(e) => setActivityType(e.target.value)}
                        className={cn(LD_NATIVE_SELECT, 'h-10 shrink-0 mt-0')}
                      >
                        <option value="CALL">Call</option>
                        <option value="WHATSAPP">WhatsApp</option>
                        <option value="MEETING">Meeting</option>
                        <option value="EMAIL">Email</option>
                        <option value="NOTE">Note</option>
                      </select>
                      <Input
                        value={activitySummary}
                        onChange={(e) => setActivitySummary(e.target.value)}
                        placeholder="Note (optional with file upload)…"
                        className={cn(LD_INPUT, 'flex-1 min-w-[160px]')}
                      />
                      <button
                        type="button"
                        disabled={
                          agencyViewOnly || uploadLeadAttachmentMut.isPending || !leadDetail
                        }
                        className="ld-btn-outline ld-motion shrink-0"
                        onClick={() => activityFileRef.current?.click()}
                      >
                        <Paperclip className="h-4 w-4 mr-2 shrink-0 inline" aria-hidden />
                        {uploadLeadAttachmentMut.isPending ? 'Uploading…' : 'Attach file'}
                      </button>
                      <button
                        type="button"
                        disabled={agencyViewOnly}
                        className="ld-btn-primary ld-motion shrink-0 w-auto px-5"
                        onClick={() => {
                          if (agencyViewOnly) return;
                          if (!activitySummary.trim()) return;
                          addActivityMut.mutate({
                            id: leadDetail.id,
                            data: { type: activityType, summary: activitySummary.trim() },
                          });
                        }}
                      >
                        Add
                      </button>
                    </div>
                    <p className="ld-muted text-[11px] leading-snug mt-2">
                      Attach patient photos or documents (PDF, Word, Excel, images — max 15&nbsp;MB). Optional note above is
                      saved with the file on the timeline.
                    </p>
                  </LeadDetailCard>
                </aside>
              </div>
              </div>
            </>
          ) : (
            <div className="flex flex-1 min-h-[40vh] flex-col items-center justify-center gap-2 bg-[var(--ld-cream)] px-6 py-16 text-center text-[var(--ld-ink-soft)]">
              <p className="text-sm font-medium">Loading lead…</p>
              <p className="text-xs ld-muted">
                {selectedLead?.patientName} · {formatLeadPhone(selectedLead?.phone)}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
